import React, { useState, useEffect, useCallback, useRef } from 'react'
import dayjs from 'dayjs'
import axios from 'axios'
import { useTranslation } from 'react-i18next'
import { SearchCalendar } from './SearchCalendar'
import { searchData, CheckInDateType, PlanType } from './type'
import { RoomInfo } from '../room-info'

type PlanListType = PlanType[]

type DateInDateModalProps = {
  plan: PlanType
  checkInDate: CheckInDateType
  handleCloseModal: () => void
}

const DateInDateModal = (props: DateInDateModalProps) => {
  const { plan, checkInDate, handleCloseModal } = props
  const { t } = useTranslation()
  const [isLoading, setIsLoading] = useState(false)

  const goToReservationPage = () => {
    setIsLoading(true)
    const params = {
      plan_id: String(plan.planId),
      number_of_adults: String(searchData.number_of_adults),
      number_of_preschoolers: String(searchData.number_of_preschoolers),
      number_of_students: String(searchData.number_of_students),
      number_of_preschoolers_1: String(searchData.number_of_preschoolers_1),
      number_of_students_1: String(searchData.number_of_students_1),
      start_of_stay: checkInDate.date,
      end_of_stay: checkInDate.endDate,
    }

    const query = new URLSearchParams(params).toString()
    window.location.href = '/reservations/new?' + query
  }

  const number = (value) => (value ? Number(value) : 0)

  const childrenText = () => {
    const texts: string[] = []

    if (number(searchData.number_of_preschoolers) > 0) {
      texts.push(t('reservation_input.modal.plan_detail.preschoolers', { count: searchData.number_of_preschoolers }))
    }
    if (number(searchData.number_of_students) > 0) {
      texts.push(t('reservation_input.modal.plan_detail.students', { count: searchData.number_of_students }))
    }
    if (number(searchData.number_of_preschoolers_1) > 0) {
      texts.push(t('reservation_input.modal.plan_detail.preschoolers_1', { count: searchData.number_of_preschoolers_1 }))
    }
    if (number(searchData.number_of_students_1) > 0) {
      texts.push(t('reservation_input.modal.plan_detail.students_1', { count: searchData.number_of_students_1 }))
    }
    return texts.join('、')
  }

  return (
    <div className="modal-backdrop fixed top-0 left-0 right-0 z-50 w-full overflow-x-hidden overflow-y-auto max-h-full h-full justify-center items-center flex md:inset-0">
      <div
        className="flex flex-col items-start p-6 bg-white rounded-lg w-[350px] md:w-[580px]"
        style={{ animation: '0.4s ease-in-out 0s 1 normal forwards running slide-down' }}
      >
        <div className="flex flex-col items-start gap-4 w-full">
          <div className="flex items-start gap-3 w-full">
            <div className="flex-1 font-bold font-size-16">
              {t('reservation_confirm.modal_booking.title.text_1')}
              <br />
              {t('reservation_confirm.modal_booking.title.text_2')}
            </div>
            <button
              className="text-gray-900 bg-transparent hover:bg-gray-200 rounded-lg text-sm ml-auto inline-flex items-center dark:hover:bg-gray-600 dark:hover:text-white p-1.5"
              data-modal-hide="number-personal-modal"
              type="button"
              onClick={handleCloseModal}
            >
              <svg className="w-5 h-5" fill="currentColor" viewBox="0 0 20 20" xmlns="http://www.w3.org/2000/svg">
                <path
                  clipRule="evenodd"
                  d="M4.293 4.293a1 1 0 011.414 0L10 8.586l4.293-4.293a1 1 0 111.414 1.414L11.414 10l4.293 4.293a1 1 0 01-1.414 1.414L10 11.414l-4.293 4.293a1 1 0 01-1.414-1.414L8.586 10 4.293 5.707a1 1 0 010-1.414z"
                  fillRule="evenodd"
                />
              </svg>
            </button>
          </div>
          <div className="flex flex-col items-start gap-3 w-full">
            <div className="flex items-center gap-5 w-full">
              <div className="flex flex-col items-start gap-1 flex-1">
                <div className="text-green font-size-12 font-bold">{t('plan_detail.navigation.nursery_info')}</div>
                <div className="font-bold font-size-14">{plan.mainNurseryName}</div>
              </div>
              <div className="h-20">
                <img
                  src={plan.mainNurseryFirstFeatureImageUrl}
                  alt={plan.mainNurseryName}
                  className="reservation-confirm-image"
                />
              </div>
            </div>
            {plan.subNurseryName && (
              <div className="flex items-center gap-5 w-full">
                <div className="flex flex-col items-start gap-1 flex-1">
                  <div className="text-green font-size-12 font-bold">{t('plan_detail.navigation.nursery_info')}</div>
                  <div className="font-bold font-size-14">{plan.subNurseryName}</div>
                </div>
                <div className="h-20">
                  <img
                    src={plan.subNurseryFirstFeatureImageUrl}
                    alt={plan.subNurseryName}
                    className="reservation-confirm-image"
                  />
                </div>
              </div>
            )}
            <hr className="w-full border-gray-200 my-4"></hr>
            <div className="flex items-center gap-5 w-full">
              <div className="flex flex-col items-start gap-1 flex-1">
                <div className="text-green font-size-12 font-bold">{t('plan_detail.navigation.accommodation')}</div>
                <div className="font-bold font-size-14">{plan.roomMaster.name}</div>
              </div>
              <div className="h-20">
                <img src={plan.roomMaster.images[0]} alt={plan.roomMaster.name} className="reservation-confirm-image" />
              </div>
            </div>
            <hr className="w-full border-gray-200 my-4" />
            <div className="flex flex-col items-start gap-1 w-full">
              <div className="text-green font-size-12 font-bold">{t('reservation_confirm.modal_booking.booking_info')}</div>
              <div className="font-size-12 text-black-600">
                {t('reservation_confirm.modal_booking.period')}
                {dayjs(new Date(checkInDate.date)).format('M/D')}〜{dayjs(new Date(checkInDate.endDate)).format('M/D')}
                <br />
                {t('reservation_input.modal.plan_detail.people')}
                {t('reservation_input.modal.plan_detail.adult', { count: searchData.number_of_adults })}
                {childrenText()}
              </div>
            </div>
            <hr className="w-full border-gray-200 my-4"></hr>
            <div className="inline-flex items-center gap-5">
              <div className="flex flex-col items-start gap-1">
                <div className="text-green font-size-12 font-bold">{t('reservation_confirm.price.total')}</div>
                <div className="font-bold font-size-14">
                  {checkInDate.price.toLocaleString('ja-JP')}
                  {t('reservation_confirm.modal_booking.currency')}
                </div>
              </div>
            </div>
          </div>
        </div>
        {isLoading ? (
          <button
            type="button"
            className="box-border w-full text-gray-900 focus:outline-none hover:bg-green-800 focus:ring-4 focus:ring-green-300 font-bold rounded-lg text-sm h-11 px-3 py-2 mt-6 dark:bg-gray-800 dark:text-white dark:bg-green-600 dark:hover:bg-green-700 dark:focus:ring-green-800 request-processing"
            disabled
            aria-busy="true"
          >
            <svg
              className="inline w-4 h-4 mr-3 text-black animate-spin"
              viewBox="0 0 100 101"
              fill="none"
              xmlns="http://www.w3.org/2000/svg"
            >
              <path
                d="M100 50.5908C100 78.2051 77.6142 100.591 50 100.591C22.3858 100.591 0 78.2051 0 50.5908C0 22.9766 22.3858 0.59082 50 0.59082C77.6142 0.59082 100 22.9766 100 50.5908ZM9.08144 50.5908C9.08144 73.1895 27.4013 91.5094 50 91.5094C72.5987 91.5094 90.9186 73.1895 90.9186 50.5908C90.9186 27.9921 72.5987 9.67226 50 9.67226C27.4013 9.67226 9.08144 27.9921 9.08144 50.5908Z"
                fill="#E5E7EB"
              />
              <path
                d="M93.9676 39.0409C96.393 38.4038 97.8624 35.9116 97.0079 33.5539C95.2932 28.8227 92.871 24.3692 89.8167 20.348C85.8452 15.1192 80.8826 10.7238 75.2124 7.41289C69.5422 4.10194 63.2754 1.94025 56.7698 1.05124C51.7666 0.367541 46.6976 0.446843 41.7345 1.27873C39.2613 1.69328 37.813 4.19778 38.4501 6.62326C39.0873 9.04874 41.5694 10.4717 44.0505 10.1071C47.8511 9.54855 51.7191 9.52689 55.5402 10.0491C60.8642 10.7766 65.9928 12.5457 70.6331 15.2552C75.2735 17.9648 79.3347 21.5619 82.5849 25.841C84.9175 28.9121 86.7997 32.2913 88.1811 35.8758C89.083 38.2158 91.5421 39.6781 93.9676 39.0409Z"
                fill="currentColor"
              />
            </svg>
          </button>
        ) : (
          <button
            type="button"
            className="box-border h-11 px-3 py-2 w-full bg-green rounded-lg mt-6 text-white font-size-14 font-bold"
            onClick={goToReservationPage}
          >
            {t('reservation_confirm.modal_booking.button')}
          </button>
        )}
      </div>
    </div>
  )
}

const CheckInDate = (props: { plan: PlanType; checkInDate: CheckInDateType }) => {
  const { plan, checkInDate } = props
  const [showModal, setShowModal] = useState<Boolean>(false)
  const ref = useRef<HTMLDivElement>(null)
  const { t } = useTranslation()

  useEffect(() => {
    let dateView = searchData.start_date
    if (!dateView) {
      const urlParams = new URLSearchParams(window.location.search)
      const monthStrings = urlParams.get('select_month') || '' // "2025/08,2025/06,2025/10"
      const months = monthStrings.split(',')
      if (months.length) {
        const selected = plan.checkInDates.find((date) => {
          const checkInMonthString = dayjs(new Date(date.date)).format('YYYY/MM')
          return months.includes(checkInMonthString)
        })

        if (checkInDate === selected) dateView = checkInDate.date
      }
    }

    if (dateView === checkInDate.date) {
      const parent = ref.current.parentElement
      parent.scrollLeft = ref.current.offsetLeft - parent.offsetLeft
    }
  }, [searchData])

  const handleCloseModal = () => setShowModal(false)

  const displayPrice = (checkInDate: CheckInDateType) => {
    if (!checkInDate.price) {
      return '-'
    }

    const price = checkInDate.price / 10000
    const displayPrice = Number.isInteger(price) ? String(price.toFixed(0)) : price.toFixed(1)
    return `${displayPrice}${t('reservation_confirm.modal_booking.currency_man')}${t(
      'reservation_confirm.modal_booking.currency',
    )}`
  }

  return (
    <>
      {showModal && <DateInDateModal plan={plan} checkInDate={checkInDate} handleCloseModal={handleCloseModal} />}
      <div
        ref={ref}
        style={{ width: '84px' }}
        className={`bg-white flex flex-col gap-2 items-center justify-between p-3 text-center transition-all duration-300 h-24 flex-shrink-0 rounded-md ${
          checkInDate.available ? 'border-green border cursor-pointer' : 'border border-gray-300 cursor-not-allowed'
        }`}
        onClick={() => (checkInDate.available && checkInDate.price ? setShowModal(true) : null)}
      >
        <div className={`text-xs ${checkInDate.available ? 'text-green' : 'text-gray-400'}`}>
          {dayjs(new Date(checkInDate.date)).format('M/D')}({t('day.formats.short.sunday')})
        </div>
        <div className={checkInDate.available ? 'available' : 'unavailable'}>
          {checkInDate.available ? (
            <svg fill="none" height="20" viewBox="0 0 20 20" width="20" xmlns="http://www.w3.org/2000/svg">
              <rect height="18" rx="9" stroke="#189855" strokeWidth="2" width="18" x="1" y="1" />
            </svg>
          ) : (
            <svg fill="none" height="16" viewBox="0 0 16 16" width="16" xmlns="http://www.w3.org/2000/svg">
              <path
                d="M2.85 1.03L8 6.17l5.12-5.12a1.33 1.33 0 011.88 1.88L9.78 8l5.19 5.19a1.33 1.33 0 01-1.88 1.88L8 9.83l-5.13 5.13a1.33 1.33 0 01-1.88-1.88L6.21 8 1.03 2.81A1.33 1.33 0 112.85 1.03z"
                fill="#D1D5DB"
              />
            </svg>
          )}
        </div>
        <div className={`text-xs whitespace-nowrap truncate w-full ${checkInDate.available ? 'text-green' : 'text-gray-400'}`}>
          {displayPrice(checkInDate)}
        </div>
      </div>
    </>
  )
}

const Plan = (props: { plan: PlanType }) => {
  const { plan } = props
  const { roomMaster, checkInDates } = plan
  const [isOpenDetail, setIsOpenDetail] = useState(false)
  const { t } = useTranslation()

  if (!roomMaster) {
    return <></>
  }

  return (
    <>
      {isOpenDetail && <RoomInfo roomMaster={roomMaster} close={() => setIsOpenDetail(false)} />}
      <div>
        <div className="flex gap-15px items-center justify-start ml-4 md:ml-0">
          <img src={roomMaster.images[0]} alt={roomMaster.name} className="w-full h-auto" style={{ width: '120px' }} />
          <div className="flex flex-col justify-between">
            <div className="text-green font-bold underline mb-2 cursor-pointer" onClick={() => setIsOpenDetail(true)}>
              {roomMaster.name}
            </div>
            {roomMaster.description && <div className="text-gray-500 mb-2">{roomMaster.description}</div>}
            <div className="flex gap-2">
              <div className="bg-gray-200 px-2 py-1 rounded-md text-xs">
                {t('landing_page.avaiability.room_master.max_capacity', { count: roomMaster.roomMaxCount })}
              </div>
              {roomMaster.foodText && <div className="bg-gray-200 p-1 rounded-md text-xs">{`食事${roomMaster.foodText}`}</div>}
            </div>
          </div>
        </div>
        <div className="flex gap-2 overflow-auto hidden-scrollbar mt-5 mb-6">
          {checkInDates.map((checkInDate, index) => (
            <CheckInDate plan={plan} checkInDate={checkInDate} key={index} />
          ))}
        </div>
      </div>
    </>
  )
}

const PlanList = (props: { planList: PlanListType }) => {
  const { planList } = props
  const roomTypeOrders = ['保育園留学の寮', '一棟貸し', 'アパートメントホテル', 'ホテル', '旅館', 'ゲストハウス', '民宿']
  const plansGroupedByRoomType = roomTypeOrders
    .map((roomType) => ({
      roomType,
      plans: planList.filter((plan) => plan.roomMaster.roomType === roomType),
    }))
    .filter((group) => group.plans.length > 0)

  if (planList.length === 0) {
    const consultationImageUrl = document.getElementById('lp-availability')?.dataset?.consultationImageUrl
    return (
      <>
        <p className="px-4 pb-4 font-semibold leading-6">現在の条件では該当する空き日程がありません。</p>
        <div id="consultation">
          <div className="consultation-wrapper">
            <div className="image">
              <img src={consultationImageUrl} alt="consultation" className="rounded-12" />
            </div>
            <div className="content">
              <div className="block w-full">
                <h3 className="font-size-18 tracking-wider font-a1 text-green text-left md:text-center mb-2 md:font-size-28">
                  留学先選びに迷ったら
                </h3>
                <div className="font-size-14 tracking-wider gallery-item-text text-left md:text-center">
                  保育園留学に行きたいけれど、どんなふうに留学先を選んだら良いかわからない
                </div>
                <div className="font-size-14 tracking-wider gallery-item-text text-left md:text-center mb-6">
                  そんな方のために、さまざまなサービスをご用意しております。
                </div>
              </div>
              <div className="flex flex-wrap justify-center text-center gap-2 consultation-margin">
                <div className="circle-text">
                  <div className="font-size-12 md:font-size-20">
                    留学先の
                    <br />
                    ご提案
                  </div>
                </div>
                <div className="circle-text">
                  <div className="font-size-12 md:font-size-20">
                    トータル予算の
                    <br />
                    ご案内
                  </div>
                </div>
                <div className="circle-text">
                  <div className="font-size-12 md:font-size-20">
                    留学プランの
                    <br />
                    ご相談
                  </div>
                </div>
              </div>
              <div className="block consultation-margin w-full">
                <h3 className="font-size-18 tracking-wider font-a1 text-green text-left md:text-center mb-2 md:font-size-28">
                  LINEで相談
                </h3>
                <div className="text-left mb-4 md:text-center md:mb-8">
                  <p className="font-size-14 tracking-wider gallery-item-text">
                    チャット（会話）でいただいたご質問に、プランナーがお答えします。
                  </p>
                  <p className="font-size-14 tracking-wider gallery-item-text">【受付】平日 9:00 - 18:00（土日祝休）</p>
                </div>
                <button className="bg-green text-white w-full text-center px-6 py-3 rounded-md font-size-14 mx-auto block md:font-size-16 md:py-4">
                  <a
                    href="https://liff.line.me/2002843084-E1bqwPzZ/landing?follow=%40007nfzzj&lp=CxMUwm&liff_id=2002843084-E1bqwPzZ"
                    target="_blank"
                    rel="noopener"
                  >
                    LINEで相談する（お友だち追加）
                  </a>
                </button>
              </div>
              <div className="block mb-10 w-full">
                <h3 className="font-size-18 tracking-wider font-a1 text-green text-left md:text-center mb-2 md:font-size-28">
                  オンライン対話で相談
                </h3>
                <p className="font-size-14 tracking-wider gallery-item-text">
                  オンライン面談で保育園留学のプランニングをサポートします。下部の予約システムより、ご希望の日時をご予約ください。
                </p>
                <p className="font-size-14 tracking-wider gallery-item-text">
                  なお、すでに留学のご予約がお済みでオンライン面談を希望される場合、下記リンクより面談のご予約をお願いいたします。
                </p>
                <p className="font-size-14 tracking-wider gallery-item-text text-green">
                  【ご予約後の方】保育園留学プランナーとのオンライン面談予約はこちら　＞
                </p>
                <br />
                <p className="font-size-14 tracking-wider gallery-item-text">【所要時間】30分程度</p>
                <p className="font-size-14 tracking-wider gallery-item-text">※事前に注意事項をご確認ください▽</p>
                <p className="font-size-14 tracking-wider gallery-item-text">
                  ①予約フォームにご要望を詳しく記入いただくと、最適なプランをご案内しやすくなります
                </p>
                <p className="font-size-14 tracking-wider gallery-item-text">
                  ②ご夫婦での参加をおすすめしています（お一人での参加もOK）
                </p>
                <p className="font-size-14 tracking-wider gallery-item-text">
                  ③ご希望のお時間帯のご予約ができない場合、LINEでご相談いただくことで調整可能な場合もございます（対応時間：9時〜18時）。お友だち登録の上お気軽にご相談ください
                </p>
              </div>
              <div className="block">
                {/* Spir widget begin */}
                <div
                  className="spir-widget mx-auto"
                  data-url="https://app.spirinc.com/external/teams/6QiOmA1OT5rJKNwk3dVRP/availability-sharings/x7hTLhME1lD9pYRiFK_R4/confirm"
                  style={{ height: '300px' }}
                >
                  <iframe
                    width="100%"
                    name="spir-widget-iframe-0"
                    height="100%"
                    frameBorder="0"
                    src="https://app.spirinc.com/external/teams/6QiOmA1OT5rJKNwk3dVRP/availability-sharings/x7hTLhME1lD9pYRiFK_R4/confirm"
                  ></iframe>
                  <script async="" src="https://app.spirinc.com/js/external/iframe.js" type="text/javascript"></script>
                </div>
                {/* Spir widget end */}
              </div>
            </div>
          </div>
        </div>
      </>
    )
  }

  return (
    <>
      {plansGroupedByRoomType.map((group, index) => (
        <div key={index} className="mb-4">
          <div className="font-size-18 font-bold mb-3 ml-4 md:ml-0">{group.roomType}</div>
          {group.plans.map((plan, planIndex) => (
            <Plan plan={plan} key={planIndex} />
          ))}
        </div>
      ))}
    </>
  )
}

export const LpAvailability = () => {
  const [planList, setPlanList] = useState<PlanListType>([])
  const [loading, setLoading] = useState(false)

  const fetchData = useCallback(async () => {
    try {
      setLoading(true)
      const query = new URLSearchParams(location.search)
      const queryParams = Object.fromEntries(query.entries())

      const params = {
        ...searchData,
        ...queryParams,
        start_date: queryParams.start_date || '',
      }
      Object.assign(searchData, { ...params })

      const { data } = await axios.get(location?.pathname, {
        headers: { 'X-Requested-With': 'XMLHttpRequest' },
        params,
      })

      setPlanList(data.plans)
    } catch (err) {
      console.error(err)
    } finally {
      setLoading(false)
    }
  }, [location?.pathname])

  useEffect(() => {
    fetchData()
  }, [])

  useEffect(() => {
    axios.get(location.pathname + '/header_price' + location.search).then((res) => {
      if (res.data) {
        const element = document.getElementsByClassName('price-info-block')[0] as HTMLElement
        element.outerHTML = res.data
      }
    })
  }, [location.search])

  return (
    <div className="flex flex-col-reverse md:flex-row content-new">
      <div className="info-block conLeft w-full md:w-[65%]">
        <div id="availability">
          <p className="availability-title text-left font-a1 font-size-20 md:font-size-28">空き日程</p>
          {loading ? (
            <div className="flex flex-col">
              <PlanSkeleton />
              <PlanSkeleton />
            </div>
          ) : (
            <PlanList planList={planList} />
          )}
        </div>
      </div>
      <div className="conRight w-full md:w-[35%]">
        <div className="calendar-info-block h-fit bg-white w-full md:relative md:sticky md:mt-0">
          <div className="calendar-info-content">
            <SearchCalendar onChange={fetchData} />
          </div>
        </div>
      </div>
    </div>
  )
}

// Skeleton component for plan items
const PlanSkeleton = () => {
  return (
    <>
      <div className="mb-2 animate-pulse-card">
        <div className="h-4 w-32 bg-gray-200 rounded mb-4"></div>
        <div className="flex gap-5 items-center justify-start">
          <div className="w-[120px] h-[80px] bg-gray-200 rounded"></div>
          <div className="flex-1">
            <div className="h-4 w-40 bg-gray-200 rounded mb-2"></div>
            <div className="h-3 w-64 bg-gray-200 rounded mb-3"></div>
            <div className="flex gap-2 mt-2">
              <div className="h-6 w-24 bg-gray-200 rounded"></div>
              <div className="h-6 w-20 bg-gray-200 rounded"></div>
            </div>
          </div>
        </div>
        <div className="flex gap-2 overflow-auto mt-5 mb-6">
          {[...Array(6)].map((_, index) => (
            <div
              key={index}
              className="flex flex-col gap-2 items-center justify-between p-3 text-center w-18 h-24 bg-gray-200 rounded-md"
              style={{ animationDelay: `${index * 0.1}s` }}
            >
              <div className="h-3 w-10 bg-gray-300 rounded"></div>
              <div className="h-5 w-5 bg-gray-300 rounded-full"></div>
              <div className="h-3 w-12 bg-gray-300 rounded"></div>
            </div>
          ))}
        </div>
      </div>
    </>
  )
}
